export const plutoTree = {
  id: 'pluto',
  label: 'Pluto',
  type: 'Dwarf planet',
  summary: 'Kuiper Belt world, reclassified in 2006, still loved, still weird.',
  payload: [
    {
      type: 'description',
      text: "Pluto was discovered in 1930 by Clyde Tombaugh and considered the ninth planet for 76 years. In 2006 the IAU reclassified it as a dwarf planet after the discovery of several similarly-sized bodies (Eris, Makemake, Haumea) in the Kuiper Belt made planethood untenable without admitting them too. New Horizons flew past in July 2015 and revealed a world far more complex than anyone expected: nitrogen-ice glaciers, 3-km mountains of water ice, a thin atmosphere that expands and collapses with its seasons, and a heart-shaped region named Tombaugh Regio whose western lobe is a vast plain of nitrogen ice."
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '2,376 km (smaller than our Moon)' },
        { label: 'Mass',              value: '1.303 × 10²² kg (0.00218 × Earth)' },
        { label: 'Distance from Sun', value: '5,906 M km mean (39.5 AU)' },
        { label: 'Orbital period',    value: '248 Earth years' },
        { label: 'Day length',        value: '6 days 9h (retrograde)' },
        { label: 'Axial tilt',        value: '119.5° (spins on its side and backwards)' },
        { label: 'Moons',             value: '5 (Charon, Styx, Nix, Kerberos, Hydra)' },
        { label: 'Surface temp',      value: '−247 to −233 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'A small, icy world with a peculiar orbit',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Radius',            value: '1,188 km' },
            { label: 'Mean density',      value: '1.854 g/cm³' },
            { label: 'Surface gravity',   value: '0.62 m/s² (0.063 g)' },
            { label: 'Escape velocity',   value: '1.21 km/s' }
          ]
        },
        {
          type: 'stats',
          label: 'Orbit',
          entries: [
            { label: 'Perihelion',        value: '4,437 M km (29.66 AU)' },
            { label: 'Aphelion',          value: '7,376 M km (49.31 AU)' },
            { label: 'Eccentricity',      value: '0.2488 (very elliptical)' },
            { label: 'Inclination',       value: '17.16° (tilted relative to ecliptic)' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: "Pluto's orbit is so eccentric that it spends 20 years of each 248-year orbit INSIDE Neptune's orbit. That happened most recently from 1979 to 1999, so for two decades Pluto was the 8th planet."
        }
      ]
    },
    {
      id: 'surface',
      label: 'Surface',
      summary: 'Nitrogen glaciers, ice mountains, the famous heart',
      payload: [
        {
          type: 'description',
          text: "New Horizons' 2015 flyby revealed a surface that no one had predicted. Sputnik Planitia, the western lobe of the heart-shaped Tombaugh Regio, is a basin more than 1,000 km across filled with nitrogen ice, convecting like a glacier in ultra-slow motion. Its craterless surface is no more than about 100 million years old, which means Pluto is still geologically active. The plain is broken into cells separated by troughs, each cell the top of a convecting nitrogen column. To its west are mountain ranges made of water ice (the only material strong enough to form mountains at 38 K), some 3 km tall."
        },
        {
          type: 'list',
          entries: [
            { primary: 'Tombaugh Regio',  secondary: 'The heart, about 1,600 km across; its western lobe is the nitrogen-ice plain' },
            { primary: 'Sputnik Planitia', secondary: 'Western lobe of the heart, convecting nitrogen glacier' },
            { primary: 'Cthulhu Macula',   secondary: 'Dark equatorial region, tholins stained the surface' },
            { primary: 'Tartarus Dorsa',   secondary: '"Snakeskin" terrain of methane-ice blades, 500 m tall' }
          ]
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Thin nitrogen, expands and freezes with the seasons',
      payload: [
        {
          type: 'description',
          text: "Pluto has a tenuous atmosphere of mostly nitrogen, with trace amounts of methane and carbon monoxide. At about 13 microbar at the surface it is roughly 80,000× thinner than Earth's. As Pluto moves further from the Sun along its 248-year orbit, the atmosphere literally condenses and snows onto the surface. When Pluto returns toward perihelion, the ices sublime back into gas. Pluto passed perihelion in 1989, so the atmosphere is currently thicker than it will be for another two centuries, so we've been lucky to study it now."
        }
      ]
    },
    {
      id: 'moons',
      label: 'Moons',
      summary: 'Charon is so large relative to Pluto that the pair is really a double dwarf',
      payload: [
        {
          type: 'list',
          entries: [
            { primary: 'Charon',   secondary: '1,212 km diameter, half of Pluto\'s size; the two orbit a common barycentre outside Pluto' },
            { primary: 'Styx',     secondary: '~10 km, chaotic rotation' },
            { primary: 'Nix',      secondary: '~50 km, tumbling unpredictably' },
            { primary: 'Kerberos', secondary: '~19 km, double-lobed' },
            { primary: 'Hydra',    secondary: '~62 km long, outer moon' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: "Pluto and Charon are tidally locked to EACH OTHER: Charon always hangs in the same spot in Pluto's sky, and vice versa. A hypothetical colony on the Charon-facing side of Pluto would always see Charon overhead; on the far side they'd never see it at all."
        }
      ]
    },
    {
      id: 'new-horizons',
      label: 'New Horizons',
      summary: 'The only mission to Pluto, which flew past on July 14, 2015',
      payload: [
        {
          type: 'description',
          text: "New Horizons launched January 19, 2006, making it the fastest spacecraft ever to leave Earth (58,536 km/h). Despite that, the trip to Pluto still took 9.5 years. Closest approach was 12,500 km at 11:49:57 UTC on July 14, 2015. Because of the distance, every image required 4.5 hours to travel back at light speed, and the full dataset took about 16 months to transmit at low bandwidth. It then continued into the Kuiper Belt and flew past Arrokoth (2014 MU69) on New Year's Day 2019."
        },
        {
          type: 'stats',
          entries: [
            { label: 'Launch',         value: 'Jan 19, 2006' },
            { label: 'Closest approach', value: 'Jul 14, 2015, 11:49:57 UTC' },
            { label: 'Flyby distance',  value: '12,500 km' },
            { label: 'Relative speed',  value: '13.78 km/s' }
          ]
        }
      ]
    },
    {
      id: 'demotion',
      label: 'The 2006 reclassification',
      summary: 'Why Pluto isn\'t a planet any more',
      payload: [
        {
          type: 'description',
          text: "The IAU's 2006 definition of 'planet' requires three criteria: orbits the Sun, is roughly spherical under self-gravity, and has 'cleared the neighbourhood' around its orbit. Pluto fails the third: it shares its orbital region with countless Kuiper Belt objects, including some nearly its size. Rather than accept Eris, Haumea, Makemake, Ceres and others as planets (potentially adding dozens more as surveys improved), the IAU created the 'dwarf planet' category. Pluto was reclassified on August 24, 2006, to lasting public annoyance."
        },
        {
          type: 'callout',
          tone: 'info',
          text: "The IAU vote was 237 to 157, with only about 4% of IAU members present. The New Horizons principal investigator Alan Stern remains the leading critic and advocates a 'geophysical' planet definition that would restore Pluto and add many more worlds."
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA Pluto overview',      url: 'https://science.nasa.gov/dwarf-planets/pluto/' },
            { label: 'NASA Pluto fact sheet',    url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/plutofact.html' },
            { label: 'NASA: frozen plains in the heart', url: 'https://www.nasa.gov/news-release/nasas-new-horizons-discovers-frozen-plains-in-the-heart-of-plutos-heart/' },
            { label: 'Wikipedia: Pluto',          url: 'https://en.wikipedia.org/wiki/Pluto' },
            { label: 'New Horizons mission',      url: 'http://pluto.jhuapl.edu/' }
          ]
        }
      ]
    }
  ]
};
